<template>
  <div class="report-popup">
    <div class="header">
      Report
      <img
        @click="handleClose"
        class="close-icon"
        src="@/assets/images/close-icon.svg"
        alt=""
      >
    </div>
    <div class="body">
      <div class="title-wrapper">
        <div class="title">
          Found a Problematic? Report It Now
        </div>
        <div class="sub-title">
          We’ll address it promptly to protect your purchasing interests
        </div>
      </div>
      <div
        v-if="reportSuccess"
        class="report-success"
      >
        <img
          src="@/assets/illus/illus-report.svg"
          alt=""
        >
        <div class="text-1">
          Report Submitted Successfully
        </div>
        <div class="text-2">
          Thank you for reporting this. We will expedite the review.
          Meanwhile, we will limit exposure/access to this [product/supplier] to prevent other customers from encountering similar problems.
        </div>
      </div>
      <template v-else>
        <div class="reasons-wrapper">
          <div class="title">
            Reason for Reporting
          </div>
          <div class="reasons">
            <div
              v-for="(item, index) in reportReasons"
              :key="index"
              @click="activeIndex = index"
              :class="{ 'active': index === activeIndex }"
              class="reason-item"
            >
              {{ item.desc }}
            </div>
          </div>
        </div>
        <div class="additional-info">
          <div class="title">
            With Additional Info
          </div>
          <textarea
            v-model="additional"
            maxlength="500"
            placeholder="Please provide additional description to help us investigate this report. (Optional)"
            type="text"
          />
        </div>
      </template>
      <div class="footer">
        <div
          v-if="reportSuccess"
          @click="handleOk"
          class="button submit"
        >
          Ok
        </div>
        <template v-else>
          <div
            @click="handleClose"
            class="button cancel"
          >
            Cancel
          </div>
          <div
            @click="handleSubmit"
            class="button submit"
          >
            Submit
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { fetchData } from '@/utils/fetch'
import { buriedPointUtil } from '@/utils/utils'

const emits = defineEmits(['close'])

const props = defineProps({
  type: {
    type: String,
    default: 'PRODUCT'
  },
  targetId: {
    type: String,
    default: ''
  }
})

const reportReasons = ref([])

const activeIndex = ref(0)

const additional = ref('')

const reportSuccess = ref(false)

const getReasons = async () => {
  const { data } = await fetchData('/appb/b2b/report/getReportReason?type=' + props.type)
  reportReasons.value = data
}

const handleOk = () => {
  emits('close')
  location.reload()
}

const handleClose = () => {
  emits('close')
  if (reportSuccess.value) {
    location.reload()
  }
}

const handleSubmit = async () => {
  const params = {
    type: props.type,
    reportUrl: location.href,
    targetId: props.targetId,
    reasonCode: reportReasons.value[activeIndex.value].code,
    additionalInfo: additional.value
  }
  const { code } = await fetchData('/appb/b2b/report/save', {
    method: 'post',
    body: JSON.stringify(params)
  })
  if (code === 200) {
    reportSuccess.value = true
    buriedPointUtil({
      eventId: 'HPD207',
      others: {
        type: props.type === 'PRODUCT' ? 'Products' : 'Factory',
        objectId: props.targetId
      }
    })
  }
}

onMounted(() => {
  getReasons()
})

</script>

<style lang='less' scoped>

.report-popup {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  height: 100%;
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    padding: 0 12px;
    border-radius: 8px;
    background-color: white;
    color: #232323;
    font-size: 14px;
    font-family: mon-700;
  }
  .body {
    position: relative;
    flex: 1;
    background-color: white;
    border-radius: 8px;
    padding: 0 12px;

    .title-wrapper {
      margin-top: 16px;
      .title {
        color: #232323;
        font-size: 14px;
        font-family: mon-700;
      }
      .sub-title {
        margin-top: 4px;
        color: #232323;
        font-size: 12px;
        font-family: mon-500;
      }
    }

    .report-success {
      margin-top: 24px;
      .text-1 {
        margin-top: 8px;
        line-height: 24px;
        color: #232323;
        font-size: 16px;
        font-family: mon-700;
      }
      .text-2 {
        color: #232323;
        font-size: 12px;
        font-family: mon-500;
        line-height: 18px;
        margin-top: 3px;
      }
    }

    .reasons-wrapper {
      margin-top: 24px;
      .title {
        color: #232323;
        font-size: 12px;
        font-family: mon-700;
      }
      .reasons {
        margin-top: 12px;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
        .reason-item {
          min-width: 0;
          height: 64px;
          padding: 0 8px;
          border-radius: 4px;
          border: 1px solid #ebf1f5;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: #232323;
          font-size: 12px;
          font-family: mon-500;
          &.active {
            border: 2px solid #232323;
          }
        }
      }
    }
    .additional-info {
      margin-top: 24px;
      .title {
        color: #232323;
        font-size: 12px;
        font-family: mon-700;
      }
      textarea {
        margin-top: 12px;
        padding: 12px;
        border-radius: 4px;
        border: 1px solid #ebf1f5;
        outline: none;
        resize: none;
        height: 80px;
        width: 100%;

        &::placeholder {
          color: #b2b7c2;
          font-size: 11px;
          font-family: mon-500;
        }

        &:focus {
          border: 1.5px solid #232323;
        }
      }
    }
    .footer {
      position: absolute;
      bottom: 0;
      height: 72px;
      width: 100%;
      border-top: 1px solid #ebf1f5;
      padding: 12px 12px 0;
      margin-left: -12px;
      display: flex;
      gap: 12px;
      .button {
        flex: 1;
        height: 44px;
        border-radius: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-family: mon-600;
        &.cancel {
          background-color: #f8f8f8;
          color: #232323;
        }
        &.submit {
          background-color: #052784;
          color: white;
        }
      }
    }
  }
}
</style>
